import React from "react";
import LegalLayout from "@/components/LegalLayout";
import { openCookieConsent } from "@/components/CookieConsent";
import { Cookie } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";

export default function Cookies() {
  const { t, lang } = useI18n();
  return (
    <LegalLayout
      kicker={t("legal.cookies_kicker")}
      title={t("legal.cookies_title")}
      lastUpdated={lang === "da" ? "Maj 2026" : "May 2026"}
    >
      <div className="not-prose tf-card p-5 flex items-start gap-4 mb-10" data-testid="cookie-manage-card">
        <div className="w-10 h-10 flex items-center justify-center border shrink-0" style={{ borderColor: "var(--tf-border)" }}>
          <Cookie weight="duotone" size={22} color="#FFCC00" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg tracking-tight">
            {lang === "da" ? "Administrer dine cookie-præferencer" : "Manage your cookie preferences"}
          </div>
          <div className="text-sm mt-1" style={{ color: "var(--tf-text-2)" }}>
            {lang === "da"
              ? "Du kan til enhver tid genåbne cookie-banneret og opdatere dit valg."
              : "Open the cookie banner again any time to update your choice."}
          </div>
          <button
            type="button"
            data-testid="cookie-reopen"
            onClick={openCookieConsent}
            className="kinetic-btn mt-4 inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#007AFF] text-white"
          >
            {lang === "da" ? "Åbn cookie-indstillinger" : "Open cookie settings"}
          </button>
        </div>
      </div>

      {lang === "da" ? <DanishCookies /> : <EnglishCookies />}
    </LegalLayout>
  );
}

function EnglishCookies() {
  return (
    <>
      <p>
        This Cookie Policy explains how TeamFit uses cookies and similar technologies on our website and services.
      </p>
      <p>By using TeamFit, you consent to the use of cookies as described in this policy.</p>

      <h2>What Are Cookies?</h2>
      <p>Cookies are small text files stored on your device when visiting a website or using online services.</p>
      <p>Cookies help improve:</p>
      <ul>
        <li>Functionality</li>
        <li>Performance</li>
        <li>User experience</li>
        <li>Security</li>
      </ul>

      <h2>Types of Cookies We Use</h2>

      <h3>Essential Cookies</h3>
      <p>Required for:</p>
      <ul>
        <li>Login/authentication</li>
        <li>Security</li>
        <li>Core website functionality</li>
      </ul>
      <p>These cookies cannot be disabled.</p>

      <h3>Analytics Cookies</h3>
      <p>Used to understand:</p>
      <ul>
        <li>Website traffic</li>
        <li>User behavior</li>
        <li>App and website performance</li>
      </ul>
      <p>This helps us improve TeamFit.</p>

      <h3>Functional Cookies</h3>
      <p>Used to remember:</p>
      <ul>
        <li>Language preferences</li>
        <li>Dark/light mode</li>
        <li>User settings</li>
        <li>Session preferences</li>
      </ul>

      <h3>Notification &amp; Preference Cookies</h3>
      <p>Help manage:</p>
      <ul>
        <li>Notification settings</li>
        <li>Login sessions</li>
        <li>Personalized experiences</li>
      </ul>

      <h2>Third-Party Services</h2>
      <p>TeamFit may use third-party services such as:</p>
      <ul>
        <li>Google</li>
        <li>Apple</li>
        <li>Analytics providers</li>
        <li>Map providers</li>
      </ul>
      <p>These services may use their own cookies according to their own policies.</p>

      <h2>Managing Cookies</h2>
      <p>Users can:</p>
      <ul>
        <li>Accept or reject non-essential cookies</li>
        <li>Change browser cookie settings</li>
        <li>Delete cookies anytime</li>
      </ul>
      <p>Disabling cookies may affect some features of TeamFit.</p>

      <h2>Changes to This Policy</h2>
      <p>We may update this Cookie Policy occasionally.</p>
      <p>Changes will be published on: <a href="https://teamfit.dk">teamfit.dk</a></p>

      <h2>Contact</h2>
      <p>
        TeamFit<br />
        Website: <a href="https://teamfit.dk">teamfit.dk</a><br />
        Email: <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>
      </p>
    </>
  );
}

function DanishCookies() {
  return (
    <>
      <p>
        Denne cookiepolitik forklarer, hvordan TeamFit bruger cookies og lignende teknologier på vores hjemmeside og tjenester.
      </p>
      <p>Ved at bruge TeamFit accepterer du brugen af cookies som beskrevet i denne politik.</p>

      <h2>Hvad er cookies?</h2>
      <p>Cookies er små tekstfiler, der gemmes på din enhed, når du besøger en hjemmeside eller bruger onlinetjenester.</p>
      <p>Cookies hjælper med at forbedre:</p>
      <ul>
        <li>Funktionalitet</li>
        <li>Ydeevne</li>
        <li>Brugeroplevelse</li>
        <li>Sikkerhed</li>
      </ul>

      <h2>Typer af cookies vi bruger</h2>

      <h3>Nødvendige cookies</h3>
      <p>Påkrævet for:</p>
      <ul>
        <li>Login/autentificering</li>
        <li>Sikkerhed</li>
        <li>Grundlæggende hjemmeside-funktionalitet</li>
      </ul>
      <p>Disse cookies kan ikke deaktiveres.</p>

      <h3>Analytics-cookies</h3>
      <p>Bruges til at forstå:</p>
      <ul>
        <li>Hjemmesidetrafik</li>
        <li>Brugeradfærd</li>
        <li>App- og hjemmeside-ydeevne</li>
      </ul>
      <p>Dette hjælper os med at forbedre TeamFit.</p>

      <h3>Funktionelle cookies</h3>
      <p>Bruges til at huske:</p>
      <ul>
        <li>Sprogpræferencer</li>
        <li>Mørk/lys-tilstand</li>
        <li>Brugerindstillinger</li>
        <li>Sessionspræferencer</li>
      </ul>

      <h3>Notifikations- og præference-cookies</h3>
      <p>Hjælper med at administrere:</p>
      <ul>
        <li>Notifikationsindstillinger</li>
        <li>Login-sessioner</li>
        <li>Personaliserede oplevelser</li>
      </ul>

      <h2>Tredjepartstjenester</h2>
      <p>TeamFit kan bruge tredjepartstjenester såsom:</p>
      <ul>
        <li>Google</li>
        <li>Apple</li>
        <li>Analytics-udbydere</li>
        <li>Kortudbydere</li>
      </ul>
      <p>Disse tjenester kan bruge deres egne cookies i henhold til deres egne politikker.</p>

      <h2>Administration af cookies</h2>
      <p>Som bruger kan du:</p>
      <ul>
        <li>Acceptere eller afvise ikke-nødvendige cookies</li>
        <li>Ændre cookie-indstillinger i browseren</li>
        <li>Slette cookies når som helst</li>
      </ul>
      <p>Deaktivering af cookies kan påvirke nogle funktioner i TeamFit.</p>

      <h2>Ændringer af denne politik</h2>
      <p>Vi kan opdatere denne cookiepolitik fra tid til anden.</p>
      <p>Ændringer vil blive offentliggjort på: <a href="https://teamfit.dk">teamfit.dk</a></p>

      <h2>Kontakt</h2>
      <p>
        TeamFit<br />
        Hjemmeside: <a href="https://teamfit.dk">teamfit.dk</a><br />
        E-mail: <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>
      </p>
    </>
  );
}
